import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import {
  Play,
  CheckCircle2,
  XCircle,
  Clock,
  FileText,
  ArrowRight,
  Plus,
  Droplets,
  Zap,
  Hash,
  Flame,
  Gauge,
  Thermometer,
  Wind,
  Layers,
  Search,
  CheckCheck,
} from "lucide-react";

const PRODUCT_TYPE_ICONS = {
  droplets: Droplets,
  flame: Flame,
  gauge: Gauge,
  thermometer: Thermometer,
  zap: Zap,
  wind: Wind,
};

const getProductIcon = (iconName) => PRODUCT_TYPE_ICONS[iconName] || Layers;

export default function TesterDashboard() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [myTests, setMyTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewTest, setShowNewTest] = useState(false);
  const [creating, setCreating] = useState(false);
  const [testStep, setTestStep] = useState("product"); // "product" | "details"
  const [productSearch, setProductSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const [newTest, setNewTest] = useState({
    product_id: "",
    power_supply: null,
    serial_number: "",
    custom_values: {},
    step_variables: {},
  });
  const [dynamicVars, setDynamicVars] = useState([]); // [{var_name, label, step_name}]

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, testsRes, typesRes] = await Promise.all([
          axios.get(`${API}/products`, { headers }),
          axios.get(`${API}/tests`, { headers }),
          axios.get(`${API}/product-types`, { headers }),
        ]);
        setProducts(productsRes.data);
        setMyTests(testsRes.data);
        setProductTypes(typesRes.data);
        if (productsRes.data.length > 0) {
          setNewTest(prev => ({ ...prev, product_id: productsRes.data[0].id, custom_values: {} }));
        }
      } catch (error) {
        toast.error("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [headers]);

  const handleStartTest = async () => {
    if (!newTest.product_id) {
      toast.error("Please select a product");
      return;
    }
    if (requiresSerialNumber && !newTest.serial_number?.trim()) {
      toast.error("Serial number is required");
      return;
    }
    if (selectedProduct?.power_options?.length > 0 && !newTest.power_supply) {
      toast.error("Please select power supply");
      return;
    }
    for (const field of testInputFields) {
      const val = newTest.custom_values[field.key];
      const isEmpty = val === "" || val === undefined || val === null;
      if (field.required && isEmpty) {
        toast.error(`${field.label} is required`);
        return;
      }
      if (field.type === "number" && !isEmpty) {
        const num = Number(val);
        if (isNaN(num)) {
          toast.error(`${field.label} must be a valid number`);
          return;
        }
        if (field.min !== undefined && field.min !== "" && num < Number(field.min)) {
          toast.error(`${field.label} must be at least ${field.min}`);
          return;
        }
        if (field.max !== undefined && field.max !== "" && num > Number(field.max)) {
          toast.error(`${field.label} must be at most ${field.max}`);
          return;
        }
      }
    }
    
    setCreating(true);
    try {
      const response = await axios.post(`${API}/tests`, newTest, { headers });
      toast.success("Test session created!");
      setShowNewTest(false);
      navigate(`/tester/verify/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create test session");
    } finally {
      setCreating(false);
    }
  };

  const loadDynamicVars = async (productId) => {
    try {
      const res = await axios.get(`${API}/steps?product_id=${productId}`, { headers });
      const vars = [];
      const seen = new Set();
      for (const step of res.data) {
        // OCR dynamic rules
        const rules = step.ocr_validation?.rules || [];
        for (const rule of rules) {
          if (rule.type === "dynamic" && rule.value && !seen.has(rule.value)) {
            seen.add(rule.value);
            vars.push({ var_name: rule.value, label: rule.label || rule.value, step_name: step.name });
          }
        }
        // {{var}} in description or pass_criteria
        const text = `${step.description || ""} ${step.pass_criteria || ""}`;
        const matches = [...text.matchAll(/\{\{(\w+)\}\}/g)];
        for (const m of matches) {
          if (!seen.has(m[1])) {
            seen.add(m[1]);
            vars.push({ var_name: m[1], label: m[1], step_name: step.name });
          }
        }
      }
      setDynamicVars(vars);
    } catch {
      setDynamicVars([]);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "passed": return <CheckCircle2 className="w-5 h-5 text-emerald-500" />;
      case "failed": return <XCircle className="w-5 h-5 text-red-500" />;
      case "in_progress": return <Clock className="w-5 h-5 text-amber-500" />;
      default: return <Clock className="w-5 h-5 text-slate-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      passed: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
      failed: "bg-red-500/10 text-red-600 border-red-500/20",
      in_progress: "bg-amber-500/10 text-amber-600 border-amber-500/20"
    };
    return (
      <Badge variant="outline" className={variants[status] || "bg-slate-500/10"}>
        {status.replace("_", " ")}
      </Badge>
    );
  };

  const selectedProduct = products.find(p => p.id === newTest.product_id);
  const selectedProductType = productTypes.find(t => t.id === selectedProduct?.product_type_id);
  const requiresSerialNumber = selectedProductType?.require_serial_number !== false;
  const fieldOptionConfig = selectedProduct?.field_option_config || {};
  // For select fields: use only the options configured for this product (if any); fall back to all options
  const disabledFieldKeys = new Set(selectedProduct?.disabled_fields || []);
  const testInputFields = (selectedProductType?.default_test_input_fields || [])
    .filter(f => !disabledFieldKeys.has(f.key))
    .map(f => {
      if (f.type === "select" && fieldOptionConfig[f.key]?.length > 0) {
        return { ...f, options: fieldOptionConfig[f.key] };
      }
      return f;
    });
  const matchesSearch = (test) => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    return (
      test.product_name?.toLowerCase().includes(q) ||
      test.model_name?.toLowerCase().includes(q) ||
      test.serial_number?.toLowerCase().includes(q)
    );
  };
  const inProgressTests = myTests.filter(t => t.status === "in_progress" && matchesSearch(t));
  const completedTests = myTests.filter(t => t.status !== "in_progress" && matchesSearch(t));

  // Get labelled custom_values entries for a test
  const getTestTags = (test) => {
    const product = products.find(p => p.id === test.product_id);
    const productType = productTypes.find(t => t.id === product?.product_type_id);
    const fields = productType?.default_test_input_fields || [];
    const tags = [];
    if (test.power_supply) tags.push({ label: "Power", value: test.power_supply });
    const cv = test.custom_values || {};
    for (const [key, val] of Object.entries(cv)) {
      if (!val && val !== 0) continue;
      const field = fields.find(f => f.key === key);
      tags.push({ label: field?.label || key, value: String(val) });
    }
    return tags;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tester-dashboard">
      {/* Welcome & Start Test */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Welcome, {user?.name}</h2>
          <p className="text-muted-foreground">Start a new verification or continue your tests</p>
        </div>
        
        <Dialog
          open={showNewTest}
          onOpenChange={(open) => {
            setShowNewTest(open);
            if (open) setTestStep("product");
          }}
        >
          <DialogTrigger asChild>
            <Button size="lg" className="gap-2" data-testid="start-new-test-btn">
              <Plus className="w-5 h-5" />
              Start New Test
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px] max-h-[85dvh] flex flex-col overflow-hidden dialog-mobile-safe">
            <DialogHeader className="shrink-0">
              <DialogTitle>Start New Verification Test</DialogTitle>
              <DialogDescription>
                {testStep === "product"
                  ? "Choose which product you're verifying"
                  : `Verification parameters for ${selectedProduct?.name || "the selected product"}`}
              </DialogDescription>
            </DialogHeader>

            {testStep === "product" ? (
              <div className="flex flex-col gap-3 py-4 min-w-0 w-full min-h-0 flex-1 overflow-hidden">
                <div className="space-y-2 shrink-0">
                  <Label htmlFor="product-search">Product</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                    <Input
                      id="product-search"
                      placeholder="Search products…"
                      value={productSearch}
                      onChange={e => setProductSearch(e.target.value)}
                      className="pl-9"
                      autoComplete="off"
                    />
                  </div>
                </div>
                <div className="overflow-y-auto flex-1 rounded-md border bg-popover">
                  {products.filter(p => {
                    const q = productSearch.trim().toLowerCase();
                    if (!q) return true;
                    return (p.name + " " + (p.model_name || "")).toLowerCase().includes(q);
                  }).length === 0 ? (
                    <p className="py-6 text-center text-sm text-muted-foreground">No product found.</p>
                  ) : (
                    products.filter(p => {
                      const q = productSearch.trim().toLowerCase();
                      if (!q) return true;
                      return (p.name + " " + (p.model_name || "")).toLowerCase().includes(q);
                    }).map(product => {
                      const Icon = getProductIcon(product.product_type_icon);
                      const isSelected = newTest.product_id === product.id;
                      return (
                        <button
                          key={product.id}
                          type="button"
                          className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left transition-colors hover:bg-accent hover:text-accent-foreground cursor-pointer ${isSelected ? "bg-primary/10 text-primary font-medium" : ""}`}
                          onClick={() => setNewTest({ ...newTest, product_id: product.id, custom_values: {}, power_supply: null })}
                          data-testid={`product-option-${product.id}`}
                        >
                          <Icon className={`w-4 h-4 shrink-0 ${isSelected ? "text-primary" : "text-blue-500"}`} />
                          <span className="truncate flex-1">{product.name}</span>
                          {product.model_name && (
                            <span className="text-xs text-muted-foreground font-mono shrink-0">({product.model_name})</span>
                          )}
                          {isSelected && <CheckCheck className="w-4 h-4 shrink-0 text-primary" />}
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            ) : (
            <div className="space-y-4 py-4 min-w-0 w-full overflow-y-auto scrollbar-hide">
              <button
                type="button"
                onClick={() => setTestStep("product")}
                className="flex items-center gap-2 min-w-0 text-sm rounded-lg border bg-muted/40 px-3 py-2 w-full text-left hover:bg-muted transition-colors"
                data-testid="change-product-btn"
              >
                {(() => {
                  const Icon = getProductIcon(selectedProduct?.product_type_icon);
                  return <Icon className="w-4 h-4 text-blue-500 shrink-0" />;
                })()}
                <span className="truncate font-medium">{selectedProduct?.name}</span>
                {selectedProduct?.model_name && (
                  <span className="text-xs text-muted-foreground font-mono shrink-0">({selectedProduct.model_name})</span>
                )}
                <span className="ml-auto text-xs text-primary shrink-0">Change</span>
              </button>

              {selectedProduct && selectedProduct.power_options && selectedProduct.power_options.length > 0 && (
                <div className="space-y-2">
                  <Label>Power Supply <span className="text-destructive text-xs">*</span></Label>
                  <div className="flex flex-wrap gap-2">
                    {selectedProduct.power_options.map((power) => (
                      <Button
                        key={power}
                        type="button"
                        variant={newTest.power_supply === power ? "default" : "outline"}
                        className="flex-1 min-w-[7rem] text-sm"
                        onClick={() => setNewTest({ ...newTest, power_supply: power })}
                        data-testid={`power-${power.replace(/\s+/g, '-')}`}
                      >
                        <Zap className="w-4 h-4 mr-1" />
                        {power}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {testInputFields.length > 0 && (
                <div className="space-y-3 border-t pt-3">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Verification Parameters</p>
                  {testInputFields.map((field) => (
                    <div key={field.key} className="space-y-1">
                      <Label htmlFor={`custom-${field.key}`} className="flex items-center gap-1">
                        {field.label}
                        {field.required && <span className="text-destructive text-xs">*</span>}
                      </Label>
                      {field.type === "select" && field.options?.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {field.options.map((opt) => (
                            <Button
                              key={opt}
                              type="button"
                              variant={newTest.custom_values[field.key] === opt ? "default" : "outline"}
                              size="sm"
                              className="text-xs sm:text-sm h-auto py-1.5 px-3 whitespace-normal text-left"
                              onClick={() => setNewTest({
                                ...newTest,
                                custom_values: { ...newTest.custom_values, [field.key]: opt }
                              })}
                            >
                              {opt}
                            </Button>
                          ))}
                        </div>
                      ) : (
                        <Input
                          id={`custom-${field.key}`}
                          type={field.type === "number" ? "number" : "text"}
                          placeholder={field.placeholder || ""}
                          className={field.type === "number" ? "font-mono" : ""}
                          value={newTest.custom_values[field.key] || ""}
                          onChange={(e) => setNewTest({
                            ...newTest,
                            custom_values: {
                              ...newTest.custom_values,
                              [field.key]: field.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value
                            }
                          })}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}

              {dynamicVars.length > 0 && (
                <div className="space-y-3 border-t pt-3">
                  <p className="text-xs font-medium text-amber-600 dark:text-amber-400 uppercase tracking-wide">Step Variables</p>
                  <p className="text-xs text-muted-foreground">These values are used in specific verification steps.</p>
                  {dynamicVars.map((v) => (
                    <div key={v.var_name} className="space-y-1">
                      <Label htmlFor={`dynvar-${v.var_name}`} className="flex items-center gap-1">
                        {v.label}
                        <span className="text-xs text-muted-foreground font-normal">({v.step_name})</span>
                      </Label>
                      <Input
                        id={`dynvar-${v.var_name}`}
                        placeholder={`Enter ${v.label}`}
                        className="font-mono"
                        value={newTest.step_variables[v.var_name] || ""}
                        onChange={(e) => setNewTest({
                          ...newTest,
                          step_variables: { ...newTest.step_variables, [v.var_name]: e.target.value }
                        })}
                      />
                    </div>
                  ))}
                </div>
              )}

              {requiresSerialNumber && (
              <div className="space-y-2">
                <Label htmlFor="serial">Serial Number <span className="text-destructive text-xs">*</span></Label>
                <Input
                  id="serial"
                  placeholder="Enter device serial number"
                  className="font-mono"
                  value={newTest.serial_number}
                  onChange={(e) => setNewTest({ ...newTest, serial_number: e.target.value })}
                  data-testid="serial-number-input"
                />
              </div>
              )}
            </div>
            )}

            <DialogFooter className="flex-col-reverse sm:flex-row gap-2 shrink-0">
              {testStep === "product" ? (
                <>
                  <Button variant="outline" className="w-full sm:w-auto" onClick={() => setShowNewTest(false)}>Cancel</Button>
                  <Button
                    className="w-full sm:w-auto"
                    onClick={() => { setTestStep("details"); loadDynamicVars(newTest.product_id); }}
                    disabled={!newTest.product_id}
                    data-testid="continue-to-details-btn"
                  >
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" className="w-full sm:w-auto" onClick={() => setShowNewTest(false)}>Cancel</Button>
                  <Button className="w-full sm:w-auto" onClick={handleStartTest} disabled={creating} data-testid="confirm-start-test-btn">
                    {creating ? "Creating..." : "Start Verification"}
                    <Play className="w-4 h-4 ml-2" />
                  </Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search by product, model, or serial number…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-8"
          data-testid="tests-search-input"
        />
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">In Progress</CardTitle>
            <Clock className="w-4 h-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-mono text-amber-600">{inProgressTests.length}</div>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Passed</CardTitle>
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-mono text-emerald-600">
              {completedTests.filter(t => t.status === "passed").length}
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Failed</CardTitle>
            <XCircle className="w-4 h-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-mono text-red-600">
              {completedTests.filter(t => t.status === "failed").length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* In Progress Tests */}
      {inProgressTests.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-500" />
              Continue Testing
            </CardTitle>
            <CardDescription>Tests that need to be completed</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {inProgressTests.map((test) => (
                <div
                  key={test.id}
                  className="flex items-start justify-between p-4 rounded-lg border bg-amber-500/5 border-amber-500/20 hover:bg-amber-500/10 transition-colors"
                  data-testid={`continue-test-${test.id}`}
                >
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/tester/verify/${test.id}`)}>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 shrink-0 rounded-lg bg-amber-500/20 flex items-center justify-center">
                        <span className="text-base font-bold font-mono text-amber-600">
                          {test.step_results ? test.step_results.filter(sr => ["pass","fail","warning","skipped"].includes(sr.status)).length : test.current_step}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium truncate">
                          {test.product_name}
                          {test.model_name && (
                            <span className="text-xs text-muted-foreground font-mono font-normal ml-1.5">({test.model_name})</span>
                          )}
                        </p>
                        {test.serial_number && (
                          <p className="text-xs text-muted-foreground font-mono mb-1">SN: {test.serial_number}</p>
                        )}
                        <div className="flex flex-wrap gap-1">
                          {getTestTags(test).map(({ label, value }) => (
                            <span key={label} className="inline-flex items-center gap-1 text-xs bg-muted rounded px-1.5 py-0.5">
                              <span className="text-muted-foreground">{label}:</span>
                              <span className="font-medium">{value}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0 ml-2">
                    <p className="text-xs font-mono text-muted-foreground">
                      {test.step_results ? test.step_results.filter(sr => ["pass","fail","warning","skipped"].includes(sr.status)).length : test.current_step}/{test.total_steps} steps
                    </p>
                    <Button size="sm" onClick={() => navigate(`/tester/verify/${test.id}`)} data-testid={`continue-btn-${test.id}`}>
                      Continue
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Completed Tests */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Recent Tests</CardTitle>
            <CardDescription>Your completed verification sessions</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate("/tester/reports")} data-testid="view-my-reports-btn">
            View All
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </CardHeader>
        <CardContent>
          {completedTests.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchQuery.trim() ? (
                <>
                  <Search className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No completed tests match "{searchQuery}"</p>
                </>
              ) : (
                <>
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No completed tests yet</p>
                  <p className="text-sm">Start a new verification to see results here</p>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {completedTests.slice(0, 5).map((test) => (
                <div
                  key={test.id}
                  className="flex items-start justify-between p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors cursor-pointer"
                  onClick={() => navigate(`/tester/verify/${test.id}`)}
                  data-testid={`completed-test-${test.id}`}
                >
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div className="shrink-0 mt-0.5">{getStatusIcon(test.status)}</div>
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">
                        {test.product_name}
                        {test.model_name && (
                          <span className="text-xs text-muted-foreground font-mono font-normal ml-1.5">({test.model_name})</span>
                        )}
                      </p>
                      {test.serial_number && (
                        <p className="text-xs text-muted-foreground font-mono mb-1">SN: {test.serial_number}</p>
                      )}
                      <div className="flex flex-wrap gap-1">
                        {getTestTags(test).map(({ label, value }) => (
                          <span key={label} className="inline-flex items-center gap-1 text-xs bg-muted rounded px-1.5 py-0.5">
                            <span className="text-muted-foreground">{label}:</span>
                            <span className="font-medium">{value}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0 ml-2">
                    <p className="text-xs text-muted-foreground whitespace-nowrap">
                      {new Date(test.started_at).toLocaleDateString()}
                    </p>
                    {getStatusBadge(test.status)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
